import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Input, Select } from '@/components/ui';
import styles from './ProfileEditView.module.css';
import shared from './Profile.module.css';
import { useTouchedFields } from './useTouchedFields';
import {
    EMPTY_PROFILE_FORM,
    GENDER_OPTIONS,
    countMissingRequired,
    maskCccdDisplay,
    type ProfileFormValues,
} from './profileFormState';
import { splitVietnameseName } from './nameSplit';

export interface ProfileEditViewProps {
    displayName: string;
    email: string | undefined;
    /** Best-effort "role · org" line from real `/me/access` data — e.g. "org_admin,
     *  teacher · Trường Quốc tế EMG Hà Nội". Omitted (not fabricated) when there is
     *  no acting-tenant membership to derive it from. */
    roleLine?: string;
    onMissingCountChange?: (count: number) => void;
    onBack: () => void;
}

// EAA-1804: no longer positioned via `.portal-field-icon` (that class assumed
// the removed `.portal-field-control` wrapper as its `position:relative`
// anchor) — it is now `Input`'s own `suffix` slot, which positions it itself.
// Colour is kept inline so the muted/disabled look survives the drop.
function LockIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            aria-hidden="true"
            style={{ color: 'var(--color-text-disabled)' }}
        >
            <rect x="5" y="10.5" width="14" height="9" rx="2" />
            <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" strokeLinecap="round" />
        </svg>
    );
}

/**
 * `/profile` edit mode — frame 19 (`19-profile-edit.png`). Three cards
 * (định danh / địa chỉ / bổ sung), exactly the fields and copy the frame
 * gives, including the amber "Chưa chọn giới tính." error state it ships
 * pre-rendered.
 *
 * SCOPE — no PortalApi endpoint persists ANY of these fields (see
 * `profileFormState.ts`'s doc comment). This view is therefore a fully
 * functional FORM (real local state, real on-blur validation, real
 * Hoàn tác) with a save step that is honestly disabled rather than faking a
 * write — same pattern `AccountsPage` already uses for its missing
 * list-accounts endpoint. Only Họ/Tên seed from the OIDC `name` claim;
 * everything else starts blank (never a fake value).
 *
 * Tỉnh/TP + Phường/Xã: no province/ward source exists anywhere in
 * portal-api (checked — no such model/endpoint). Both selects render
 * disabled with an explanation, per the ticket's "leave the pair inert with
 * an explanation" rule, rather than a hardcoded province list.
 *
 * "Đổi ảnh": no avatar-upload endpoint exists either — same treatment.
 *
 * CMND/CCCD masks to its last 4 digits while not focused (`maskCccdDisplay`)
 * per the UX-SPEC's masking rule — a display-only mask, since (again) there
 * is no field/endpoint to enforce the real owner/admin-only reveal against.
 */
export function ProfileEditView({
    displayName,
    email,
    roleLine,
    onMissingCountChange,
    onBack,
}: ProfileEditViewProps) {
    const { t } = useTranslation();
    const seeded = splitVietnameseName(displayName);
    const initial: ProfileFormValues = { ...EMPTY_PROFILE_FORM, ho: seeded.ho, ten: seeded.ten };

    const [values, setValues] = useState<ProfileFormValues>(initial);
    const { touch, markAttempted, shouldShowError } = useTouchedFields();
    // EAA-1822: CMND/CCCD masks to its last 4 digits (UX-SPEC's "masked to the
    // last 4 digits" rule) whenever the control is not the one focused — see
    // `maskCccdDisplay`'s doc comment for why this is display-only.
    const [cccdFocused, setCccdFocused] = useState(false);

    const initials = displayName.trim().charAt(0).toUpperCase() || '?';

    function set<K extends keyof ProfileFormValues>(key: K, value: ProfileFormValues[K]) {
        setValues((prev) => {
            const next = { ...prev, [key]: value };
            onMissingCountChange?.(countMissingRequired(next, Boolean(email)));
            return next;
        });
    }

    function handleUndo() {
        setValues(initial);
        onMissingCountChange?.(countMissingRequired(initial, Boolean(email)));
    }

    function handleSaveAttempt() {
        markAttempted();
        // No write endpoint exists — see module doc comment. Intentionally a
        // no-op beyond marking fields touched, so a save attempt still
        // surfaces validation like the frame's "Chưa chọn giới tính." state.
    }

    const hoError = shouldShowError('ho') && !values.ho.trim() ? t('profile.fields.hoRequired') : null;
    const tenError = shouldShowError('ten') && !values.ten.trim() ? t('profile.fields.tenRequired') : null;
    const gioiTinhError =
        shouldShowError('gioiTinh') && !values.gioiTinh ? t('profile.fields.gioiTinhRequired') : null;

    return (
        <>
            <div className={`${shared.card} ${shared.cardFlush}`}>
                <div className={styles.editIdentity}>
                    <span className={styles.editAvatar} aria-hidden="true">
                        {initials}
                    </span>
                    <div className={styles.editIdentityInfo}>
                        <span className={styles.editName}>{displayName}</span>
                        {roleLine && <span className={shared.roleLine}>{roleLine}</span>}
                    </div>
                    {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`. */}
                    <Button disabled title={t('profile.edit.avatarUploadUnavailable')}>
                        {t('profile.edit.changeAvatar')}
                    </Button>
                </div>

                {/* EAA-1804: kit `FormField`/`Input` replacing the app-local
                    `./FormField` twin and raw `<input>`s — `helper` renamed to
                    `hint`, `is-invalid` class replaced by antd's
                    `status="error"`, and `fullWidth` (the `.portal-field-grid`
                    span, which the kit doesn't model) becomes
                    `className="portal-field-span-2"`. The read-only email
                    field's `<span className="portal-field-control"><input
                    readOnly/><LockIcon/></span>` wrapper is also dropped: kept
                    as-is, `FormField` would clone `aria-describedby`/
                    `aria-invalid` onto that `<span>` (a no-op for assistive
                    tech) instead of the input. `Input`'s own `suffix` slot
                    puts the lock glyph inside the real input's DOM node, so
                    the input is FormField's immediate child and the ARIA
                    lands where it belongs. */}
                <div className="portal-field-grid">
                    <FormField label={t('profile.fields.ho')} required error={hoError}>
                        <Input
                            status={hoError ? 'error' : undefined}
                            value={values.ho}
                            onChange={(e) => set('ho', e.target.value)}
                            onBlur={() => touch('ho')}
                        />
                    </FormField>
                    <FormField label={t('profile.fields.ten')} required error={tenError}>
                        <Input
                            status={tenError ? 'error' : undefined}
                            value={values.ten}
                            onChange={(e) => set('ten', e.target.value)}
                            onBlur={() => touch('ten')}
                        />
                    </FormField>
                    <FormField
                        label={t('profile.fields.email')}
                        required
                        className="portal-field-span-2"
                        hint={t('profile.fields.emailHelper')}
                    >
                        <Input value={email ?? ''} readOnly suffix={<LockIcon />} />
                    </FormField>
                    <FormField label={t('profile.fields.phone')}>
                        <Input
                            value={values.phone}
                            onChange={(e) => set('phone', e.target.value)}
                            placeholder={t('profile.fields.phonePlaceholder')}
                        />
                    </FormField>
                    <FormField label={t('profile.fields.cccd')} hint={t('profile.fields.cccdHelper')}>
                        <Input
                            // EAA-1822: display the masked value while blurred, the raw
                            // value while the person is actively editing it — see
                            // `maskCccdDisplay`'s doc comment (profileFormState.ts) for
                            // why this is a client-only mask.
                            value={cccdFocused ? values.cccd : maskCccdDisplay(values.cccd)}
                            onChange={(e) => set('cccd', e.target.value)}
                            onFocus={() => setCccdFocused(true)}
                            onBlur={() => setCccdFocused(false)}
                            placeholder={t('profile.fields.cccdPlaceholder')}
                            inputMode="numeric"
                            autoComplete="off"
                        />
                    </FormField>
                </div>
            </div>

            <div className={`${shared.card} ${shared.cardFlush}`}>
                <div className={styles.sectionHeader}>{t('profile.edit.addressSectionTitle')}</div>
                <div className="portal-field-grid">
                    {/* EAA-1822: Tỉnh/TP → Phường/Xã is a dependent pair per frame 19
                        (choosing Tỉnh/TP would populate Phường/Xã's options and clear
                        any stale selection), but portal-api has no geography source at
                        all to hang that on — checked every model in
                        `directory/models.py`; there is no Province/Ward model and no
                        `GET /api/v1/geo/*` route. Rendering both disabled with an
                        explicit reason rather than shipping a hardcoded province list
                        (which would silently drift from reality) or leaving them
                        fake-interactive. Would need `GET /api/v1/geo/provinces` and
                        `GET /api/v1/geo/wards?province=<code>` to implement for real. */}
                    <FormField label={t('profile.fields.tinh')} hint={t('profile.fields.tinhHelper')}>
                        <Select
                            disabled
                            value={undefined}
                            placeholder={t('profile.fields.tinhNoData')}
                            options={[]}
                        />
                    </FormField>
                    <FormField label={t('profile.fields.phuong')} hint={t('profile.fields.phuongHelper')}>
                        <Select
                            disabled
                            value={undefined}
                            placeholder={t('profile.fields.phuongPlaceholder')}
                            options={[]}
                        />
                    </FormField>
                    <FormField label={t('profile.fields.diaChiCuThe')} className="portal-field-span-2">
                        <Input
                            value={values.diaChiCuThe}
                            onChange={(e) => set('diaChiCuThe', e.target.value)}
                            placeholder={t('profile.fields.diaChiCuThePlaceholder')}
                        />
                    </FormField>
                </div>
            </div>

            <div className={`${shared.card} ${shared.cardFlush}`}>
                <div className={`${styles.sectionHeader} ${styles.sectionHeaderRow}`}>
                    <span>{t('profile.edit.additionalInfoTitle')}</span>
                    <span className={styles.sectionHint}>{t('profile.edit.additionalInfoHint')}</span>
                </div>
                <div className="portal-field-grid">
                    <FormField label={t('profile.fields.ngaySinh')}>
                        <Input
                            value={values.ngaySinh}
                            onChange={(e) => set('ngaySinh', e.target.value)}
                            placeholder={t('profile.fields.ngaySinhPlaceholder')}
                        />
                    </FormField>
                    <FormField label={t('profile.fields.gioiTinh')} required error={gioiTinhError}>
                        <Select
                            status={gioiTinhError ? 'error' : undefined}
                            placeholder={t('profile.fields.gioiTinhPlaceholder')}
                            value={values.gioiTinh === '' ? undefined : values.gioiTinh}
                            onChange={(value) => set('gioiTinh', value as ProfileFormValues['gioiTinh'])}
                            onBlur={() => touch('gioiTinh')}
                            // EAA-1845: profileFormState.ts is a plain data module (no
                            // component/hook), so it can't call useTranslation() itself —
                            // GENDER_OPTIONS.label stays as inert Vietnamese data there and
                            // the rendered text is looked up here by option value instead.
                            options={GENDER_OPTIONS.map((opt) => ({
                                value: opt.value,
                                label: t(`profile.genderOptions.${opt.value}`),
                            }))}
                        />
                    </FormField>
                </div>
            </div>

            <div className={styles.editFooter}>
                <span className={`${styles.editFooterNote} is-unavailable`}>
                    {t('profile.edit.saveUnavailableNote')}
                </span>
                {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`. */}
                <Button onClick={handleUndo}>{t('profile.edit.undo')}</Button>
                {/* EAA-1822: "Wire save" per the ticket — but there is genuinely no
                    write endpoint for ANY field group here (see this module's SCOPE
                    doc comment: every model in `directory/models.py` checked, none is
                    a per-user profile row). The nearest shape this screen would need
                    is `PATCH /api/v1/me/profile`, which does not exist. Rather than
                    faking a success toast, this stays honestly disabled — the footer
                    note states that explicitly instead of pretending "Lưu lần cuối"
                    has a real timestamp behind it. `handleSaveAttempt` still runs on
                    click (kept for the day the endpoint exists and this is simply
                    re-enabled) so a save attempt surfaces validation exactly like the
                    frame's pre-rendered "Chưa chọn giới tính." state. */}
                <Button
                    type="primary"
                    onClick={handleSaveAttempt}
                    disabled
                    title={t('profile.edit.saveUnavailableTitle')}
                >
                    {t('profile.edit.saveChanges')}
                </Button>
            </div>

            <Button onClick={onBack}>{t('profile.edit.backToProfile')}</Button>
        </>
    );
}

export default ProfileEditView;
